import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Camera as CameraIcon,
    Upload,
    Loader2,
    RefreshCcw,
    WifiOff,
    Wifi,
    Trash2,
    CheckCircle2,
} from "lucide-react";
import { api, fetchPhotoBlob, formatApiError } from "../../lib/api";
import { enqueueUpload, listQueued, broadcastPending, drainQueue } from "../../lib/offlineQueue";
import dict from "../../i18n/es-MX";

function blobToFormData(blob, filename, kind) {
    const fd = new FormData();
    fd.append("file", blob, filename);
    fd.append("kind", kind);
    return fd;
}

export default function OperatorCamera() {
    const cameraRef = useRef(null);
    const fileRef = useRef(null);
    const recentUrlsRef = useRef([]);

    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [recent, setRecent] = useState([]);
    const [recentLoading, setRecentLoading] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const [isOnline, setIsOnline] = useState(
        typeof navigator !== "undefined" ? navigator.onLine : true,
    );
    const [toast, setToast] = useState(null);

    const showToast = useCallback((msg, type = "info") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 2500);
    }, []);

    const refreshPending = useCallback(async () => {
        try {
            const items = await listQueued();
            setPendingCount(items.length);
        } catch {
            setPendingCount(0);
        }
    }, []);

    const loadRecent = useCallback(async () => {
        setRecentLoading(true);
        try {
            const res = await api.get("/photos/recent", { params: { limit: 5 } });
            const items = await Promise.all(
                res.data.map(async (p) => {
                    try {
                        const url = await fetchPhotoBlob(p.id);
                        return { ...p, url };
                    } catch {
                        return { ...p, url: null };
                    }
                }),
            );
            recentUrlsRef.current.forEach((u) => u && URL.revokeObjectURL(u));
            recentUrlsRef.current = items.map((i) => i.url).filter(Boolean);
            setRecent(items);
        } catch {
            setRecent([]);
        } finally {
            setRecentLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRecent();
        refreshPending();
    }, [loadRecent, refreshPending]);

    useEffect(
        () => () => {
            recentUrlsRef.current.forEach((u) => u && URL.revokeObjectURL(u));
        },
        [],
    );

    const drain = useCallback(async () => {
        const items = await listQueued();
        if (items.length === 0) return 0;
        const { synced } = await drainQueue();
        await refreshPending();
        if (synced > 0) {
            await loadRecent();
            showToast(`${dict.common.ok} • ${dict.operator.synced}`, "success");
        }
        return synced;
    }, [loadRecent, refreshPending, showToast]);

    useEffect(() => {
        const onOnline = () => {
            setIsOnline(true);
            drain();
        };
        const onOffline = () => setIsOnline(false);
        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);
        return () => {
            window.removeEventListener("online", onOnline);
            window.removeEventListener("offline", onOffline);
        };
    }, [drain]);

    const handleFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (preview?.url) URL.revokeObjectURL(preview.url);
        setPreview({
            blob: file,
            filename: file.name || `foto-${Date.now()}.jpg`,
            contentType: file.type || "image/jpeg",
            url: URL.createObjectURL(file),
        });
    };

    const clearPreview = () => {
        if (preview?.url) URL.revokeObjectURL(preview.url);
        setPreview(null);
    };

    const handleUpload = async () => {
        if (!preview) return;
        setUploading(true);
        const offline = !navigator.onLine;
        if (offline) {
            await enqueueUpload({
                blob: preview.blob,
                filename: preview.filename,
                contentType: preview.contentType,
                kind: "operator_field",
            });
            await refreshPending();
            await broadcastPending();
            showToast(dict.operator.queued, "info");
            clearPreview();
            setUploading(false);
            return;
        }
        try {
            await api.post(
                "/photos/upload",
                blobToFormData(preview.blob, preview.filename, "operator_field"),
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            showToast(dict.operator.uploaded, "success");
            clearPreview();
            await loadRecent();
        } catch (err) {
            if (!err?.response) {
                await enqueueUpload({
                    blob: preview.blob,
                    filename: preview.filename,
                    contentType: preview.contentType,
                    kind: "operator_field",
                });
                await refreshPending();
                await broadcastPending();
                showToast(dict.operator.queued, "info");
                clearPreview();
            } else {
                showToast(formatApiError(err) || dict.operator.uploadFailed, "error");
            }
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="px-4 py-5 space-y-5" data-testid="operator-field-view">
            <div className="flex items-center justify-between">
                <h1 className="font-display text-3xl font-extrabold tracking-tight">
                    {dict.operator.cameraTitle}
                </h1>
                <span
                    data-testid="connection-status"
                    className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border ${
                        isOnline
                            ? "border-emerald-700 bg-emerald-900/40 text-emerald-300"
                            : "border-amber-700 bg-amber-900/40 text-amber-300"
                    }`}
                >
                    {isOnline ? <Wifi size={12} /> : <WifiOff size={12} />}
                    {isOnline ? dict.common.online : dict.common.offline}
                </span>
            </div>

            <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFile}
                data-testid="camera-input"
            />
            <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFile}
                data-testid="file-input"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                    type="button"
                    onClick={() => cameraRef.current?.click()}
                    data-testid="take-photo-button"
                    className="min-h-[72px] rounded-xl bg-brand-orange hover:bg-orange-400 active:bg-orange-600 text-slate-950 font-semibold uppercase tracking-wider text-sm flex items-center justify-center gap-3 transition shadow-[0_4px_0_rgba(0,0,0,0.4)] active:translate-y-[2px] active:shadow-[0_2px_0_rgba(0,0,0,0.4)]"
                >
                    <CameraIcon size={24} />
                    {dict.operator.takePhoto}
                </button>
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    data-testid="upload-existing-button"
                    className="min-h-[72px] rounded-xl border border-slate-700 hover:border-brand-orange hover:bg-slate-900 text-slate-100 font-semibold uppercase tracking-wider text-sm flex items-center justify-center gap-3 transition"
                >
                    <Upload size={24} />
                    {dict.operator.uploadPhoto}
                </button>
            </div>

            {preview && (
                <div
                    className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden"
                    data-testid="photo-preview-card"
                >
                    <div className="px-4 py-2 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-800 flex items-center justify-between">
                        <span>{dict.operator.preview}</span>
                        <button
                            type="button"
                            onClick={clearPreview}
                            data-testid="clear-preview-button"
                            className="inline-flex items-center gap-1 text-slate-400 hover:text-red-400"
                        >
                            <Trash2 size={14} />
                            {dict.operator.clear}
                        </button>
                    </div>
                    <img
                        src={preview.url}
                        alt={dict.operator.preview}
                        className="w-full max-h-80 object-contain bg-slate-950"
                        data-testid="photo-preview-image"
                    />
                    <div className="p-3">
                        <button
                            type="button"
                            onClick={handleUpload}
                            disabled={uploading}
                            data-testid="confirm-upload-button"
                            className="w-full min-h-[52px] rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 font-semibold uppercase tracking-wider text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition"
                        >
                            {uploading ? (
                                <Loader2 className="animate-spin" size={18} />
                            ) : (
                                <CheckCircle2 size={18} />
                            )}
                            {uploading ? dict.operator.uploading : dict.operator.upload}
                        </button>
                    </div>
                </div>
            )}

            <div
                className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900/50 px-4 py-3"
                data-testid="pending-uploads-row"
            >
                <div className="text-sm">
                    <span className="text-slate-400">{dict.operator.pendingUploads}: </span>
                    <span
                        data-testid="pending-uploads-count"
                        className="font-bold text-brand-orange"
                    >
                        {pendingCount}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={drain}
                    disabled={pendingCount === 0 || !isOnline}
                    data-testid="sync-now-button"
                    className="inline-flex items-center gap-2 h-10 px-3 rounded-md border border-slate-700 hover:border-brand-orange hover:bg-slate-800 text-sm transition disabled:opacity-50"
                >
                    <RefreshCcw size={14} />
                    {dict.operator.syncNow}
                </button>
            </div>

            <section>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-display text-xl font-bold">{dict.operator.recent}</h2>
                    {recentLoading && <Loader2 className="animate-spin text-slate-500" size={16} />}
                </div>
                {recent.length === 0 && !recentLoading ? (
                    <div
                        data-testid="recent-empty"
                        className="rounded-xl border border-dashed border-slate-700 px-4 py-8 text-center text-sm text-slate-500"
                    >
                        {dict.operator.none}
                    </div>
                ) : (
                    <ul
                        data-testid="recent-photos-list"
                        className="grid grid-cols-2 sm:grid-cols-3 gap-3"
                    >
                        {recent.map((p) => (
                            <li
                                key={p.id}
                                data-testid={`recent-photo-${p.id}`}
                                className="aspect-square rounded-lg overflow-hidden border border-slate-800 bg-slate-900"
                            >
                                {p.url ? (
                                    <img
                                        src={p.url}
                                        alt={p.original_filename}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-xs text-slate-600">
                                        ⚠
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {toast && (
                <div
                    data-testid="toast"
                    className={`fixed bottom-24 left-1/2 -translate-x-1/2 px-4 py-2 rounded-md text-sm shadow-lg z-50 ${
                        toast.type === "error"
                            ? "bg-red-700 text-white"
                            : toast.type === "success"
                            ? "bg-emerald-700 text-white"
                            : "bg-slate-800 text-slate-100 border border-slate-700"
                    }`}
                >
                    {toast.msg}
                </div>
            )}
        </div>
    );
}
